import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { em } from 'polished'

import { FormContainer } from '../Form/Form.styles'
import PhoneCTA from '../PhoneCTA'
import Section from '../Section'
import Typography from '../Typography'

import background from './background.svg'

export const MegaFormWrapper = styled(Section)`
  background-image: url(${background});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top;

  /* Overriding the form container max-width inside of mega form so that the form can fill the entire mega form container. */

  ${FormContainer} {
    max-width: 700px;
  }
`

export const MegaForm = styled.div(
  ({ theme }) =>
    css`
      box-shadow: ${theme.shadow};
      position: relative;
      max-width: 830px;
      margin: auto;
    `
)

export const Heading = styled(Typography)(
  ({ theme }) =>
    css`
      margin: 0;
      background-color: ${theme.colors.secondary.base.hex};
      border-radius: ${theme.radius} ${theme.radius} 0 0;
      /* left/right padding is based off icon size, not theme spacing */
      padding: ${theme.spacing.sm};
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      color: ${theme.colors.secondary.base.readable};

      h3 {
        margin: 0;
      }

      @media (min-width: ${theme.breakpoints.sm}) {
        /* left/right padding is based off icon size, not theme spacing */
        padding: ${theme.spacing.sm} 60px;
      }
    `
)

export const Container = styled.div(
  ({ theme }) =>
    css`
      padding: ${theme.spacing.sm};
      background-color: #fff;

      h3,
      h4,
      h5,
      h6 {
        text-align: center;
      }

      @media (min-width: ${theme.breakpoints.md}) {
        padding: ${theme.spacing.sm} ${theme.spacing.xxl};
      }
    `
)

/* Instead of using a grid for the button wrapper we are
   now using flex. Each MegaFormButton has a top and left
   margin and the container has the same top left margin
   but it's negative to negate the extra spacing added
   from the buttons on the top and left side. Justify
   content center will center any rows that aren't full. */

export const StepWrapper = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr); /* Initial setting for 2 columns */
    gap: 20px;

    .step-button-wrapper {
      span {
        font-size: ${em(theme.fonts.body.md.size)};
      }

      .leshen-mega-form-step-button {
        width: 100%;
        height: 100%;
        padding: ${theme.spacing.sm} ${theme.spacing.md};
      }
    }

    @media (min-width: ${theme.breakpoints.md || '768px'}) {
      grid-template-columns: repeat(3, 1fr);

      .step-button-wrapper span {
        font-size: ${em(theme.fonts.body.lg.size)};
      }
    }
  `
)

export const Dots = styled.div(
  ({ theme }) => css`
    padding: ${theme.spacing.md};
    display: flex;
    justify-content: center;
    align-items: center;
  `
)

export const Dot = styled.div(
  ({ theme, active }) => css`
    background-color: ${theme.colors.light.dark.hex};
    display: block;
    margin-left: 3px;
    margin-right: 3px;
    border-radius: 50%;
    height: 10px;
    width: 10px;

    ${active &&
    `
      background-color: ${theme.colors.dark.lighter.hex};
    `}
  `
)

// Loading animations for the LoadingStep component
export const Spinner = styled.div(
  ({ theme }) => css`
    margin: ${theme.spacing.xs} 0;
    max-height: 50px;

    @keyframes blob-left-anim {
      0% {
        background-color: ${theme.colors.secondary.base.hex};
        transform: scale(1) translate(0, 0);
      }
      33% {
        background-color: ${theme.colors.primary.base.hex};
        transform: scale(0.55, 0.5) translate(80px, 0);
      }
      66% {
        transform: scale(0.8) translate(0, 0);
      }
      100% {
        background-color: ${theme.colors.secondary.base.hex};
        transform: scale(1) translate(0, 0);
      }
    }

    @keyframes blob-right-anim {
      0% {
        background-color: ${theme.colors.secondary.base.hex};
        transform: scale(1) translate(0, 0);
      }
      33% {
        background-color: ${theme.colors.primary.base.hex};
        transform: scale(0.55, 0.5) translate(-80px, 0);
      }
      66% {
        transform: scale(0.8) translate(0, 0);
      }
      100% {
        background-color: ${theme.colors.secondary.base.hex};
        transform: scale(1) translate(0, 0);
      }
    }
  `
)

export const Blobs = styled.div`
  filter: url('#goo');
  display: block;
  height: 50px;
  width: 200px;
  margin: auto;
`

export const Blob = styled.div(
  ({ left, theme }) => css`
    background-color: ${theme.colors.light.darker.hex};
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;

    ${left === true
      ? `animation: blob-left-anim cubic-bezier(0.77, 0, 0.175, 1) 2s infinite;`
      : `animation: blob-right-anim cubic-bezier(0.77, 0, 0.175, 1) 2s infinite;
`}
  `
)

export const leftBlob = styled.div()

const fadeInKeyFrame = `
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`

export const LoadingHeading = styled(Typography)`
  text-align: center;
  opacity: 0;
  animation: fadeIn ease 1s;
  animation-fill-mode: forwards;

  ${fadeInKeyFrame}
`

export const Bullet = styled.p(
  ({ animationDelay }) => css`
    opacity: 0;
    animation: fadeIn ease 1s;

    /* the "animation-fill-mode" and "animation-delay" properties have to come after "animation" to work properly */
    animation-fill-mode: forwards;
    animation-delay: ${animationDelay}ms;

    ${fadeInKeyFrame}
  `
)

export const ThankYouButton = styled(PhoneCTA)`
  margin: 0 auto;
`
